import api from './api';

const base = '/api/corporate/v1/enterprise/accounts';
const path = (id) => `${base}/${encodeURIComponent(id)}`;
const data = (promise) => promise.then((response) => response.data);
const params = (environment, extra = {}) => ({
  params: { environment, ...extra },
});
export const enterpriseService = {
  authorizations: () =>
    data(api.get('/api/corporate/v1/enterprise/authorizations')),
  acceptAuthorization: (id, termsVersion) =>
    data(
      api.post(
        '/api/corporate/v1/enterprise/authorizations/' +
          encodeURIComponent(id) +
          '/accept',
        { termsVersion, accepted: true }
      )
    ),
  revokeAuthorization: (id) =>
    data(
      api.delete(
        '/api/corporate/v1/enterprise/authorizations/' + encodeURIComponent(id)
      )
    ),
  remove: (id, environment, resource) =>
    data(api.delete(`${path(id)}/${resource}`, params(environment))),
  accounts: (environment = 'sandbox') =>
    data(api.get(base, params(environment))),
  discoverAccounts: async () => {
    const [production, sandbox] = await Promise.allSettled([
      enterpriseService.accounts('production'),
      enterpriseService.accounts('sandbox'),
    ]);
    // A disabled production environment is expected in dev. Other failures must
    // remain visible instead of silently selecting a different account.
    const productionDisabled =
      production.status === 'rejected' &&
      production.reason?.response?.status === 403 &&
      production.reason?.response?.data?.code === 'environment_disabled';
    if (production.status === 'rejected' && !productionDisabled)
      throw production.reason;
    if (sandbox.status === 'rejected') throw sandbox.reason;
    return {
      accounts: [
        ...(production.status === 'fulfilled' ? production.value : []),
        ...sandbox.value,
      ],
      productionEnabled: !productionDisabled,
    };
  },
  get: (id, environment) => data(api.get(path(id), params(environment))),
  read: (id, environment, resource, query = {}) =>
    data(api.get(`${path(id)}/${resource}`, params(environment, query))),
  create: (id, environment, resource, body) =>
    data(api.post(`${path(id)}/${resource}`, body, params(environment))),
  update: (id, environment, resource, body) =>
    data(api.patch(`${path(id)}/${resource}`, body, params(environment))),
  put: (id, environment, resource, body) =>
    data(api.put(`${path(id)}/${resource}`, body, params(environment))),
  download: (id, environment, companyId, documentId) =>
    api
      .get(
        `${path(id)}/companies/${encodeURIComponent(
          companyId
        )}/documents/${encodeURIComponent(documentId)}/download`,
        { ...params(environment), responseType: 'blob' }
      )
      .then((response) => response.data),
};
export const enterpriseError = (error) => {
  const code = error?.response?.data?.code;
  return (
    {
      delegation_unavailable:
        'El MCP remoto no está habilitado en este entorno.',
      delegation_exists:
        'Revoca el acceso anterior antes de autorizar este cliente otra vez.',
      verified_identity_required: 'Verifica tu correo para continuar.',
      identity_verification_unavailable:
        'No pudimos verificar tu cuenta. Intenta nuevamente.',
      scope_denied: 'Tu perfil no tiene permiso para esta acción.',
      spend_denied: 'Necesitas permiso para usar el saldo.',
      limit_exceeded: 'El importe supera tu límite autorizado.',
      invalid_expiry:
        'Selecciona un vencimiento dentro de los próximos 90 días.',
      invalid_client: 'Selecciona un cliente MCP habilitado.',
      invalid_webhook_url: 'Usa una URL pública con HTTPS y puerto 443.',
      webhook_limit: 'Ya tienes 10 webhooks. Revoca uno para agregar otro.',
      creator_unauthorized:
        'El creador perdió el acceso. Crea un nuevo webhook.',
      webhook_unavailable:
        'Activa un webhook con permisos vigentes antes de reenviar.',
      webhook_revoked: 'Este webhook fue revocado. Crea uno nuevo.',
      insufficient_balance: 'Saldo insuficiente. Recarga y vuelve a enviar.',
      invalid_amount: 'La recarga mínima es de USD 20.',
      customer_cancellation_disabled: 'Las solicitudes no se pueden cancelar.',
      company_not_found:
        'No encontramos esta empresa en la cuenta seleccionada.',
      price_changed: 'La tarifa cambió. Revisa el importe antes de enviar.',
      amount_exceeds_limit: 'El importe supera tu límite de gasto.',
      authority_required: 'Falta una autorización vigente.',
      authorization_required: 'Falta una autorización vigente.',
      idempotency_conflict: 'Esta referencia ya se usó con otros datos.',
      order_not_found:
        'No encontramos esta solicitud en la cuenta seleccionada.',
      version_conflict: 'La solicitud cambió. Actualiza para continuar.',
    }[code] ||
    error?.response?.data?.message ||
    'No pudimos completar la acción. Intenta nuevamente.'
  );
};
